import { PERMISSIONS } from '@/types/access';
import {
  ATTENTION,
  MOVE_REASON,
  PO_STATUS,
  PO_TRACKER_TAB,
  SHIPMENT_STATUS,
  type AnswerRequestBody,
  type AttentionKind,
  type EditShipmentBody,
  type MoveReason,
  type PackingListRow,
  type PoDetail,
  type PlanShipmentBody,
  type PoLineQuantities,
  type PoStatus,
  type PoTrackerList,
  type PoTrackerRow,
  type PoTrackerTab,
  type Shipment,
  type ShipmentLineInput,
  UNMATCHED_REASON,
  type UnmatchedReason,
} from '@/types/poTracker';
import { extractErrorMessage } from '@/utils/apiError';

const MS_PER_DAY = 86_400_000;
const GRACE_DAYS = 1;
const NOTE_MAX = 500;
const HTTP_PAYLOAD_TOO_LARGE = 413;
const HTTP_SERVICE_UNAVAILABLE = 503;
const BYTES_PER_KB = 1024;
const BYTES_PER_MB = BYTES_PER_KB * BYTES_PER_KB;
const MB_DECIMALS = 1;
const PACKING_LIST_MAX_MB = 10;
const PACKING_LIST_EXTENSIONS = ['.xlsx', '.csv'] as const;

export const FORWARDERS = ['NAX', 'Morrison', 'ADS'] as const;
export const PACKING_LIST_ACCEPT = PACKING_LIST_EXTENSIONS.join(',');
const PACKING_LIST_TOO_LARGE = `Choose a file of ${PACKING_LIST_MAX_MB} MB or smaller.`;

const UNMATCHED_REASON_TEXT: Record<UnmatchedReason, string> = {
  [UNMATCHED_REASON.NO_PO]: 'No PO number',
  [UNMATCHED_REASON.PO_NOT_ON_EVENT]: 'PO not on this shipment',
  [UNMATCHED_REASON.NO_LINE]: 'No matching line',
  [UNMATCHED_REASON.NO_QUANTITY]: 'No quantity',
};

const MOVE_REASON_LABEL: Record<MoveReason, string> = {
  [MOVE_REASON.WAITING_FOR_MATERIALS]: 'Waiting for materials',
  [MOVE_REASON.PRODUCTION_CAPACITY]: 'Production capacity',
  [MOVE_REASON.QUALITY_REWORK]: 'Quality issue / rework',
  [MOVE_REASON.OTHER]: 'Other',
};

const STATUS_LABEL: Record<PoStatus, string> = {
  [PO_STATUS.LATE]: 'Late',
  [PO_STATUS.SHIPPED]: 'Shipped',
  [PO_STATUS.NOT_PLANNED]: 'Not planned',
  [PO_STATUS.PARTLY_PLANNED]: 'Partly planned',
  [PO_STATUS.PLANNED]: 'Planned',
};

export interface FieldError {
  field: string;
  message: string;
}

// lineNo is carried for the error copy; left is keyed by odooLineId.
export interface LineDraft {
  odooLineId: number;
  lineNo: number;
  qty: string;
}

export function isoToday(): string {
  return new Date().toISOString().slice(0, 10);
}

// Node has no browser locale; tests pass one or stub navigator.
export function dateLocale(): string {
  return globalThis.navigator?.language ?? 'en-US';
}

// Intl orders the fields, so a US reader sees "Thu, May 28" and a UK reader "Thu 28 May".
function parts(iso: string, withWeekday: boolean, locale: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return new Intl.DateTimeFormat(locale, { timeZone: 'UTC', day: 'numeric', month: 'short', ...(withWeekday ? { weekday: 'short' } : {}) }).format(d);
}

// Every quantity on the tracker, so pieces read alike in every locale.
export function fmtPcs(n: number): string {
  return n.toLocaleString('en-US');
}

export function fmtDay(iso: string, locale: string = dateLocale()): string {
  return parts(iso, true, locale);
}

export function fmtDate(iso: string, locale: string = dateLocale()): string {
  return parts(iso, false, locale);
}

export function statusLabel(s: PoStatus): string {
  return STATUS_LABEL[s];
}

export function attentionText(a: PoTrackerRow['attention']): string | null {
  if (!a) return null;
  switch (a.kind) {
    case ATTENTION.DELIVERY_REQUESTED:
      return 'Delivery requested';
    case ATTENTION.PLAN_EXCEEDS_ORDER:
      return 'Plan exceeds order';
    case ATTENTION.PACKING_LIST_MISSING:
      return 'Packing list missing';
    case ATTENTION.OVERDUE:
      return `${fmtPcs(a.pieces ?? 0)} pcs overdue`;
    case ATTENTION.AFTER_REQUESTED:
      return `${a.days ?? 0} d after requested`;
  }
}

export type AttentionTone = 'ask' | 'late' | 'warn';

// A delivery request is a question to answer, not a problem, so it reads in its own colour;
// red is kept for lateness so it still stands out.
export function attentionTone(kind: AttentionKind): AttentionTone {
  if (kind === ATTENTION.DELIVERY_REQUESTED) return 'ask';
  return kind === ATTENTION.OVERDUE || kind === ATTENTION.AFTER_REQUESTED ? 'late' : 'warn';
}

export function pcsDeltaText(listed: number, planned: number): string {
  const diff = listed - planned;
  if (diff === 0) return '';
  return diff > 0 ? `+${fmtPcs(diff)}` : `−${fmtPcs(-diff)}`;
}

export function listStatusText(state: { loading: boolean; total: number | null; stale: boolean }): string {
  if (state.loading) return 'Loading POs…';
  if (state.total === null) return '';
  const count = state.total === 1 ? '1 PO' : `${fmtPcs(state.total)} POs`;
  return state.stale ? `${count}. Couldn't refresh, showing older data.` : count;
}

export function pagerText(list: Pick<PoTrackerList, 'page' | 'pageSize' | 'total'>): string {
  const first = (list.page - 1) * list.pageSize + 1;
  const last = Math.min(list.total, list.page * list.pageSize);
  return `${first}–${last} of ${list.total}`;
}

export function shipDatesText(row: PoTrackerRow): string {
  if (row.nextShipDate) {
    const last = row.lastShipDate ?? row.nextShipDate;
    return last === row.nextShipDate ? fmtDay(row.nextShipDate) : `${fmtDay(row.nextShipDate)} – ${fmtDay(last)}`;
  }
  // Nothing planned any more: the column pairs with the status and names the last shipped-on day.
  if (row.lastShippedOn) return `Shipped ${fmtDay(row.lastShippedOn)}`;
  return '—';
}

export function linesLabel(n: number): string {
  return n === 1 ? '1 line' : `${n} lines`;
}

export function moveReasonLabel(reason: MoveReason): string {
  return MOVE_REASON_LABEL[reason];
}

export function moveReasonText(reason: MoveReason, text: string | null): string {
  return reason === MOVE_REASON.OTHER ? `Other: ${text ?? ''}` : MOVE_REASON_LABEL[reason];
}

function dayNumber(iso: string): number {
  return Date.parse(`${iso}T00:00:00Z`) / MS_PER_DAY;
}

export function shipByFor(neededBy: string, transitDays: number): string {
  return new Date((dayNumber(neededBy) - transitDays) * MS_PER_DAY).toISOString().slice(0, 10);
}

export function daysLabel(n: number): string {
  return n === 1 ? '1 day' : `${n} days`;
}

export function fileSizeText(bytes: number): string {
  if (bytes < BYTES_PER_MB) return `${Math.ceil(bytes / BYTES_PER_KB)} KB`;
  return `${(bytes / BYTES_PER_MB).toFixed(MB_DECIMALS)} MB`;
}

export function vsRequestedText(date: string, shipBy: string): string {
  const diff = dayNumber(date) - dayNumber(shipBy);
  if (diff === 0) return 'as requested';
  return diff > 0 ? `${diff} d later than requested` : `${-diff} d earlier than requested`;
}

function qtyError(draft: LineDraft, total: number, left: Map<number, number>): FieldError | null {
  const field = `qty-${draft.odooLineId}`;
  if (!/^\d+$/.test(draft.qty.trim()) || Number(draft.qty) < 1) {
    return { field, message: `Line ${draft.lineNo}: enter a whole number of pieces above 0.` };
  }
  const available = left.get(draft.odooLineId) ?? 0;
  if (total > available) return { field, message: `Line ${draft.lineNo}: only ${fmtPcs(available)} pcs are left to plan.` };
  return null;
}

function byLineNo(lines: LineDraft[]): LineDraft[] {
  return [...lines].sort((a, b) => a.lineNo - b.lineNo);
}

export function validatePlan(input: {
  plannedDate: string;
  lines: LineDraft[];
  left: Map<number, number>;
  moved: boolean;
  reason: MoveReason | '';
  reasonText: string;
  note: string;
}): FieldError | null {
  if (!input.plannedDate) return { field: 'plannedDate', message: 'Pick a ship date.' };
  if (input.lines.length === 0) return { field: 'lines', message: 'Tick at least one line.' };
  for (const draft of byLineNo(input.lines)) {
    const err = qtyError(draft, Number(draft.qty), input.left);
    if (err) return err;
  }
  if (input.moved) {
    if (!input.reason) return { field: 'reason', message: 'Pick a reason for the new ship date.' };
    if (input.reason === MOVE_REASON.OTHER && !input.reasonText.trim()) {
      return { field: 'reasonText', message: 'Say what the other reason is.' };
    }
  }
  if (input.note.length > NOTE_MAX) return { field: 'note', message: 'Keep the note under 500 characters.' };
  return null;
}

export function validateAnswer(input: { cards: { plannedDate: string; lines: LineDraft[] }[]; left: Map<number, number>; shipBy: string; note: string }): FieldError | null {
  for (const [i, card] of input.cards.entries()) {
    if (!card.plannedDate) return { field: `card-${i}-date`, message: `Shipment ${i + 1}: pick a ship date.` };
    if (card.lines.length === 0) return { field: `card-${i}-lines`, message: 'Tick at least one line on a shipment.' };
  }
  const totals = new Map<number, number>();
  input.cards.forEach((card) => card.lines.forEach((d) => totals.set(d.odooLineId, (totals.get(d.odooLineId) ?? 0) + (Number(d.qty) || 0))));
  for (const card of input.cards) {
    for (const draft of byLineNo(card.lines)) {
      const err = qtyError(draft, totals.get(draft.odooLineId) ?? 0, input.left);
      if (err) return err;
    }
  }
  if (input.cards.some((c) => c.plannedDate > input.shipBy) && !input.note.trim()) {
    return { field: 'note', message: `Say why part of it can't ship by ${fmtDate(input.shipBy)}.` };
  }
  if (input.note.length > NOTE_MAX) return { field: 'note', message: 'Keep the note under 500 characters.' };
  return null;
}

// A ship-by in the past is allowed: a planner may ask about a PO that is already overdue.
export function validateRequest(input: { poNumber: string; neededBy: string; note: string }): FieldError | null {
  if (!input.poNumber) return { field: 'poNumber', message: 'Choose the PO you need delivered.' };
  if (!input.neededBy) return { field: 'neededBy', message: 'Pick the date you need it at viaPhoton.' };
  if (input.note.length > NOTE_MAX) return { field: 'note', message: 'Keep the note under 500 characters.' };
  return null;
}

export function requestAgeText(requestedAt: string, today: string): string {
  const days = dayNumber(today) - dayNumber(requestedAt.slice(0, 10));
  return days <= 0 ? 'today' : `${days} d ago`;
}

function usedByCards(cards: { lines: LineDraft[] }[]): Map<number, number> {
  const used = new Map<number, number>();
  cards.forEach((card) => card.lines.forEach((d) => used.set(d.odooLineId, (used.get(d.odooLineId) ?? 0) + (Number(d.qty) || 0))));
  return used;
}

// A request covers only what isn't planned or shipped yet, shared across the answer's cards.
export function leftForCard(lines: PoLineQuantities[], cards: { lines: LineDraft[] }[], index: number): Map<number, number> {
  const used = usedByCards(cards.filter((_, i) => i !== index));
  return new Map(lines.map((l) => [l.odooLineId, Math.max(0, l.toPlan - (used.get(l.odooLineId) ?? 0))]));
}

export function piecesOnNoShipment(lines: PoLineQuantities[], cards: { lines: LineDraft[] }[]): { pcs: number; lines: number } {
  const used = usedByCards(cards);
  return lines.reduce(
    (acc, l) => {
      const rest = l.toPlan - (used.get(l.odooLineId) ?? 0);
      return rest > 0 ? { pcs: acc.pcs + rest, lines: acc.lines + 1 } : acc;
    },
    { pcs: 0, lines: 0 }
  );
}

// The latest Shipped on date accepted: a day of grace for a vendor ahead of UTC.
export function latestShippedOn(today: string): string {
  return new Date((dayNumber(today) + GRACE_DAYS) * MS_PER_DAY).toISOString().slice(0, 10);
}

export function validateMarkShipped(input: { shippedOn: string; forwarder: string; shipmentIds: string[]; today: string }): FieldError | null {
  const latest = latestShippedOn(input.today);
  if (!input.shippedOn) return { field: 'shippedOn', message: 'Pick the day it shipped.' };
  if (input.shippedOn > latest) return { field: 'shippedOn', message: 'Pick today or an earlier day.' };
  if (!input.forwarder) return { field: 'forwarder', message: 'Pick a forwarder.' };
  if (input.shipmentIds.length === 0) return { field: 'shipmentIds', message: 'Tick at least one shipment.' };
  return null;
}

// Only the packing-list routes answer 413 and 503 for a reason the user can act on.
export function packingListErrorMessage(error: unknown): string {
  const status = (error as { response?: { status?: number } } | null)?.response?.status;
  if (status === HTTP_PAYLOAD_TOO_LARGE) return PACKING_LIST_TOO_LARGE;
  if (status === HTTP_SERVICE_UNAVAILABLE) return "Packing lists aren't available right now. Try again in a few minutes.";
  return extractErrorMessage(error);
}

export function leftForForm(lines: PoLineQuantities[], editing: Shipment | null): Map<number, number> {
  const own = new Map<number, number>();
  editing?.lines.forEach((l) => own.set(l.odooLineId, (own.get(l.odooLineId) ?? 0) + l.quantity));
  return new Map(lines.map((l) => [l.odooLineId, l.toPlan + (own.get(l.odooLineId) ?? 0)]));
}

export function initialDrafts(lines: PoLineQuantities[], editing: Shipment | null, left: Map<number, number>): LineDraft[] {
  if (editing) return editing.lines.map((l) => ({ odooLineId: l.odooLineId, lineNo: l.lineNo, qty: String(l.quantity) }));
  const only = lines.length === 1 ? lines[0] : null;
  const onlyLeft = only ? (left.get(only.odooLineId) ?? 0) : 0;
  return only && onlyLeft > 0 ? [{ odooLineId: only.odooLineId, lineNo: only.lineNo, qty: String(onlyLeft) }] : [];
}

export function tickAllLeft(shown: PoLineQuantities[], left: Map<number, number>, drafts: LineDraft[]): LineDraft[] {
  const next = new Map(drafts.map((d) => [d.odooLineId, d]));
  shown.forEach((l) => {
    const n = left.get(l.odooLineId) ?? 0;
    if (n > 0) next.set(l.odooLineId, { odooLineId: l.odooLineId, lineNo: l.lineNo, qty: String(n) });
  });
  return byLineNo([...next.values()]);
}

export function lineMatches(line: PoLineQuantities, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return String(line.lineNo) === q || `${line.partNumber ?? ''} ${line.description}`.toLowerCase().includes(q);
}

export function draftsTotalText(drafts: LineDraft[]): string {
  if (drafts.length === 0) return 'No lines picked yet';
  const pcs = drafts.reduce((sum, d) => sum + (Number(d.qty) || 0), 0);
  return `${linesLabel(drafts.length)} · ${fmtPcs(pcs)} pcs`;
}

function lineInputs(drafts: LineDraft[]): ShipmentLineInput[] {
  return drafts.map((d) => ({ odooLineId: d.odooLineId, quantity: Number(d.qty) }));
}

export function planBody(vendorId: string, form: { plannedDate: string; note: string; lines: LineDraft[] }): PlanShipmentBody {
  const note = form.note.trim();
  return { vendorId, plannedDate: form.plannedDate, ...(note ? { note } : {}), lines: lineInputs(form.lines) };
}

export function answerBody(cards: { plannedDate: string; lines: LineDraft[] }[], note: string): AnswerRequestBody {
  const trimmed = note.trim();
  return { cards: cards.map((c) => ({ plannedDate: c.plannedDate, lines: lineInputs(c.lines) })), ...(trimmed ? { note: trimmed } : {}) };
}

function sameLines(shipment: Shipment, drafts: LineDraft[]): boolean {
  const key = (pairs: [number, number][]): string => JSON.stringify([...pairs].sort((a, b) => a[0] - b[0]));
  return key(shipment.lines.map((l) => [l.odooLineId, l.quantity])) === key(drafts.map((d) => [d.odooLineId, Number(d.qty)]));
}

// An edit that keeps its lines isn't checked against the order (server rule), so a date or
// note change still goes through on a PO Odoo lowered or a line Odoo removed.
export function validationLeft(left: Map<number, number>, editing: Shipment | null, drafts: LineDraft[]): Map<number, number> {
  if (!editing || !sameLines(editing, drafts)) return left;
  return new Map(drafts.map((d) => [d.odooLineId, Number(d.qty)]));
}

// Only changed fields are sent: the server checks the order only on a lines edit.
export function editBody(shipment: Shipment, form: { plannedDate: string; reason: MoveReason | ''; reasonText: string; note: string; lines: LineDraft[] }): EditShipmentBody {
  const body: EditShipmentBody = {};
  if (form.plannedDate !== shipment.plannedDate) {
    body.plannedDate = form.plannedDate;
    if (form.reason) body.moveReason = form.reason;
    if (form.reason === MOVE_REASON.OTHER) body.moveReasonText = form.reasonText.trim();
  }
  const note = form.note.trim();
  if (note !== (shipment.note ?? '')) body.note = note || null;
  if (!sameLines(shipment, form.lines)) body.lines = lineInputs(form.lines);
  return body;
}

export type ShipmentState = 'planned' | 'late' | 'shipped';

export function shipmentState(s: Shipment, today: string): ShipmentState {
  if (s.status === SHIPMENT_STATUS.SHIPPED) return 'shipped';
  return s.plannedDate < today ? 'late' : 'planned';
}

function shipmentDate(s: Shipment): string {
  return s.shippedOn ?? s.plannedDate;
}

export function daysAfterRequested(s: Shipment): number {
  if (!s.requestedShipBy) return 0;
  return Math.max(0, dayNumber(shipmentDate(s)) - dayNumber(s.requestedShipBy));
}

export function sortShipments(shipments: Shipment[]): Shipment[] {
  return [...shipments].sort((a, b) => shipmentDate(a).localeCompare(shipmentDate(b)));
}

export function plannedDetailText(plannedDate: string, today: string): string {
  const diff = dayNumber(plannedDate) - dayNumber(today);
  if (diff < 0) return `Not shipped · ${-diff} d past planned date`;
  return diff === 0 ? 'Ships today' : `Ships in ${diff} d`;
}

export function poLink(origin: string, vendorId: string, poNumber: string): string {
  return `${origin}/po-tracker?${new URLSearchParams({ vendor: vendorId, po: poNumber }).toString()}`;
}

export function unmatchedReasonText(reason: UnmatchedReason): string {
  return UNMATCHED_REASON_TEXT[reason];
}

export function packingListFileError(file: Pick<File, 'name' | 'size'>): string | null {
  const name = file.name.toLowerCase();
  if (!PACKING_LIST_EXTENSIONS.some((ext) => name.endsWith(ext))) return 'Packing list must be .xlsx or .csv.';
  if (file.size > PACKING_LIST_MAX_MB * BYTES_PER_MB) return PACKING_LIST_TOO_LARGE;
  return null;
}

export interface PackingListComparisonRow {
  key: string;
  poNumber: string;
  // Lines of one PO that share a part are compared together: a packing list names the part, not the line.
  // Empty when the row's line is known to neither the event's shipments nor the PO's lines.
  lineNos: number[];
  partNumber: string | null;
  planned: number;
  listed: number;
  differs: boolean;
}

interface ComparedLine {
  odooLineId: number;
  lineNo: number;
  partNumber: string | null;
}

const comparisonKey = (poNumber: string, line: Pick<ComparedLine, 'odooLineId' | 'partNumber'>): string =>
  `${poNumber}\u0000${line.partNumber === null ? `#${line.odooLineId}` : line.partNumber.trim().toUpperCase()}`;

export function comparePackingList(eventShipments: Shipment[], rows: PackingListRow[], poDetails: Pick<PoDetail, 'poNumber' | 'lines'>[] = []): PackingListComparisonRow[] {
  const groups = new Map<string, PackingListComparisonRow>();
  const groupOf = (poNumber: string, line: ComparedLine | null, partNumber: string | null, odooLineId: number): PackingListComparisonRow => {
    const key = comparisonKey(poNumber, { odooLineId, partNumber: line?.partNumber ?? partNumber });
    const group = groups.get(key) ?? { key, poNumber, lineNos: [], partNumber: line?.partNumber ?? partNumber, planned: 0, listed: 0, differs: false };
    if (line && !group.lineNos.includes(line.lineNo)) group.lineNos = [...group.lineNos, line.lineNo].sort((a, b) => a - b);
    groups.set(key, group);
    return group;
  };
  const knownLines = new Map<number, { poNumber: string; line: ComparedLine }>();
  poDetails.forEach((d) => d.lines.forEach((line) => knownLines.set(line.odooLineId, { poNumber: d.poNumber, line })));
  eventShipments.forEach((s) =>
    s.lines.forEach((line) => {
      knownLines.set(line.odooLineId, { poNumber: s.poNumber, line });
      groupOf(s.poNumber, line, line.partNumber, line.odooLineId).planned += line.quantity;
    })
  );
  rows.forEach((r) => {
    if (r.odooLineId === null) return;
    const known = knownLines.get(r.odooLineId);
    groupOf(known?.poNumber ?? r.poNumber ?? '', known?.line ?? null, r.partNumber, r.odooLineId).listed += r.quantity ?? 0;
  });
  const firstLine = (row: PackingListComparisonRow): number => row.lineNos[0] ?? Number.MAX_SAFE_INTEGER;
  return [...groups.values()].map((row) => ({ ...row, differs: row.planned !== row.listed })).sort((a, b) => a.poNumber.localeCompare(b.poNumber) || firstLine(a) - firstLine(b));
}

// A detail may appear twice (store cache and a fresh fetch); each shipment counts once.
export function shipmentsOnEvent(eventId: string, details: Pick<PoDetail, 'shipments'>[]): Shipment[] {
  const byId = new Map(details.flatMap((d) => d.shipments.filter((s) => s.shippingEventId === eventId).map((s) => [s.id, s] as const)));
  return [...byId.values()];
}

export function noPlannedShipmentsText(po: string): string {
  return `${po} has no planned shipments. Plan one on the PO first.`;
}

export function requestsHeading(n: number): string {
  return n === 1 ? '1 delivery request needs your answer' : `${n} delivery requests need your answer`;
}

export function toPlanSummary(toPlan: number, ordered: number): string {
  return `${fmtPcs(toPlan)} of ${fmtPcs(ordered)} pcs to plan`;
}

const EMPTY_TAB_TEXT: Record<Exclude<PoTrackerTab, typeof PO_TRACKER_TAB.ALL>, string> = {
  [PO_TRACKER_TAB.LATE]: 'No late POs.',
  [PO_TRACKER_TAB.THIS_WEEK]: 'No POs ship this week.',
  [PO_TRACKER_TAB.PARTLY_PLANNED]: 'No partly planned POs.',
  [PO_TRACKER_TAB.NO_DATE]: 'Every PO has a plan.',
  [PO_TRACKER_TAB.UPDATED]: 'No updates since your last visit.',
};

export function emptyListText(state: { tab: PoTrackerTab; po: string; vendorName: string }): string {
  if (state.po) return `No PO starting with ${state.po}.`;
  if (state.tab === PO_TRACKER_TAB.ALL) return `No open POs for ${state.vendorName}.`;
  return EMPTY_TAB_TEXT[state.tab];
}

// A PO typed into Request delivery date (it isn't on the current page) must still be requestable.
export function requestPoError(detail: Pick<PoDetail, 'poNumber' | 'summary' | 'openRequest'>): string | null {
  if (detail.summary.toPlan <= 0) return `${detail.poNumber} has no pieces to plan.`;
  return detail.openRequest ? `${detail.poNumber} already has an open delivery request.` : null;
}

export function shipPoError(po: string, onDialog: string[]): string | null {
  if (!po) return 'Enter a PO number.';
  return onDialog.includes(po) ? `${po} is already on this shipment.` : null;
}

// Deduped by id so a shipment is never sent twice, whatever the cards hold.
export function tickedShipments(cards: { shipments: Shipment[] }[], ticked: Set<string>): Shipment[] {
  const byId = new Map(cards.flatMap((c) => c.shipments.filter((s) => ticked.has(s.id)).map((s) => [s.id, s] as const)));
  return [...byId.values()];
}

// Only the vendor answers; a planner (superadmins included) requests and withdraws.
export function canAnswerRequests(hasPermission: (permission: string) => boolean): boolean {
  return hasPermission(PERMISSIONS.WRITE_PO_SHIPMENTS) && !hasPermission(PERMISSIONS.REQUEST_PO_DELIVERY);
}

const HTTP_FORBIDDEN = 403;
const HTTP_NOT_FOUND = 404;

// What an expanded PO does when its detail fails to load.
export const DETAIL_FAILURE = {
  // The PO left the open list: say so and close it.
  GONE: 'gone',
  // A refresh behind a cached panel: the cache is still on screen.
  QUIET: 'quiet',
  // Nothing cached: say how to retry and close it so the next click retries.
  RETRY: 'retry',
  // A 403 is toasted by the interceptor already; just close it.
  COLLAPSE: 'collapse',
} as const;
export type DetailFailure = (typeof DETAIL_FAILURE)[keyof typeof DETAIL_FAILURE];

export function detailFailure(status: number | null, cached: boolean): DetailFailure {
  if (status === HTTP_NOT_FOUND) return DETAIL_FAILURE.GONE;
  if (cached) return DETAIL_FAILURE.QUIET;
  return status === HTTP_FORBIDDEN ? DETAIL_FAILURE.COLLAPSE : DETAIL_FAILURE.RETRY;
}

export const REQUESTS_BOX_MODE = { ANSWER: 'answer', REVIEW: 'review', NONE: 'none' } as const;
export type RequestsBoxMode = (typeof REQUESTS_BOX_MODE)[keyof typeof REQUESTS_BOX_MODE];

// Vendors get the box to answer, planners a read-only review; a read-only user gets neither.
export function requestsBoxMode(hasPermission: (permission: string) => boolean): RequestsBoxMode {
  if (canAnswerRequests(hasPermission)) return REQUESTS_BOX_MODE.ANSWER;
  if (hasPermission(PERMISSIONS.REQUEST_PO_DELIVERY)) return REQUESTS_BOX_MODE.REVIEW;
  return REQUESTS_BOX_MODE.NONE;
}

export function openRequestsSummary(n: number): string {
  return n === 1 ? '1 open delivery request' : `${n} open delivery requests`;
}

type ReviewStorage = Pick<Storage, 'getItem' | 'setItem'>;

const REQUESTS_REVIEW_KEY_PREFIX = 'mpp.poTracker.requestsReviewOpen.';

export function requestsReviewKey(user: string): string {
  return `${REQUESTS_REVIEW_KEY_PREFIX}${user}`;
}

export function readRequestsReviewOpen(storage: ReviewStorage | null, key: string): boolean {
  try {
    return storage?.getItem(key) === 'true';
  } catch {
    return false;
  }
}

export function writeRequestsReviewOpen(storage: ReviewStorage | null, key: string, open: boolean): void {
  try {
    storage?.setItem(key, open ? 'true' : 'false');
  } catch {
    // The choice still holds for this page if storage is unavailable.
  }
}

// The box lists every open request of the vendor; one off this page is answered in the single-PO view.
export function answerTarget(poNumber: string, rows: Pick<PoTrackerRow, 'poNumber'>[]): 'page' | 'single' {
  return rows.some((row) => row.poNumber === poNumber) ? 'page' : 'single';
}
